import { supabase } from '../../services/supabase'
import type { PortfolioInsight } from './portfolioInsights'

export type SpendFilters = {
  year: number | null
  bu: string
  rollup: string
}

export type SpendSummaryFilters = {
  years: number[]
  costCenters: string[]
  bus: string[]
  statuses: string[]
  departments: string[]
  rollups: string[]
  accountNames: string[]
  accountRollups: string[]
  reportRollups: string[]
  contractors: string[]
  parents: string[]
  sapItems: string[]
  materials: string[]
  plants: string[]
}

export function emptySummaryFilters(seed: SpendFilters): SpendSummaryFilters {
  return {
    years: seed.year ? [seed.year] : [],
    costCenters: [],
    bus: seed.bu ? [seed.bu] : [],
    statuses: [],
    departments: [],
    rollups: seed.rollup ? [seed.rollup] : [],
    accountNames: [],
    accountRollups: [],
    reportRollups: [],
    contractors: [],
    parents: [],
    sapItems: [],
    materials: [],
    plants: [],
  }
}

export type SpendSupplierRow = {
  parentName: string
  supplierId: string | null
  spend: number
  share: number | null
  band: string | null
  score: number | null
  priorityLine?: string | null
  linked?: boolean
}

export type SpendSlice = { key: string; spend: number; share: number | null }

export type SpendDashboard = {
  hasData: boolean
  totalSpend: number
  yoy: number | null
  activeSuppliers: number
  top10Concentration: number | null
  consultantShare: number | null
  poCount: number
  poUtilization: number | null
  highRiskSpend: number
  singleSourceSpend: number
  openMitigations: number
  byYear: { year: number; spend: number }[]
  topSuppliers: SpendSupplierRow[]
  byCategory: SpendSlice[]
  byBu: SpendSlice[]
  byBand: { high: number; medium: number; low: number; unscored: number }
  insights: PortfolioInsight[]
  years: number[]
  bus: string[]
  categories: string[]
}

export type SpendSupplierSynthesis = {
  hasData: boolean
  reason?: string
  parentName?: string
  supplierId?: string | null
  supplierName?: string | null
  location?: string | null
  category?: string | null
  region?: string | null
  linked?: boolean
  spend?: number
  share?: number | null
  yoy?: number | null
  categoryCount?: number
  poUtilization?: number | null
  consultantShare?: number | null
  score?: number | null
  band?: string | null
  singleSource?: boolean | null
  topRiskLine?: string | null
  outlook?: string | null
  openMitigations?: number
  priorityLine?: string | null
}

export type SpendCategorySynthesis = {
  hasData: boolean
  reason?: string
  category?: string
  spend?: number
  share?: number | null
  yoy?: number | null
  supplierCount?: number
  topSupplierShare?: number | null
  weightedRisk?: number | null
  suppliers?: SpendSupplierRow[]
}

function num(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

function numOrNull(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

export function formatSpend(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

export function formatSpendCompact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(0)}k`
  return formatSpend(value)
}

export function formatRatio(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  return `${(value * 100).toFixed(1)}%`
}

export function formatYoy(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  const pct = value * 100
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(1)}%`
}

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args)
  if (error) throw new Error(error.message)
  return data as T
}

export async function fetchSpendDashboard(
  companyId: string,
  filters: SpendFilters
): Promise<SpendDashboard> {
  const raw = asRecord(
    await rpc('spend_dashboard', {
      p_company_id: companyId,
      p_year: filters.year,
      p_bu: filters.bu || null,
      p_rollup_2: filters.rollup || null,
    })
  )
  if (!raw.hasData) {
    return {
      hasData: false,
      totalSpend: 0,
      yoy: null,
      activeSuppliers: 0,
      top10Concentration: null,
      consultantShare: null,
      poCount: 0,
      poUtilization: null,
      highRiskSpend: 0,
      singleSourceSpend: 0,
      openMitigations: 0,
      byYear: [],
      topSuppliers: [],
      byCategory: [],
      byBu: [],
      byBand: { high: 0, medium: 0, low: 0, unscored: 0 },
      insights: [],
      years: [],
      bus: [],
      categories: [],
    }
  }
  const band = asRecord(raw.byBand)
  const insights = Array.isArray(raw.insights) ? raw.insights : []
  return {
    hasData: true,
    totalSpend: num(raw.totalSpend),
    yoy: numOrNull(raw.yoy),
    activeSuppliers: num(raw.activeSuppliers),
    top10Concentration: numOrNull(raw.top10Concentration),
    consultantShare: numOrNull(raw.consultantShare),
    poCount: num(raw.poCount),
    poUtilization: numOrNull(raw.poUtilization),
    highRiskSpend: num(raw.highRiskSpend),
    singleSourceSpend: num(raw.singleSourceSpend),
    openMitigations: num(raw.openMitigations),
    byYear: (Array.isArray(raw.byYear) ? raw.byYear : []).map((row) => {
      const r = asRecord(row)
      return { year: num(r.year), spend: num(r.spend) }
    }),
    topSuppliers: (Array.isArray(raw.topSuppliers) ? raw.topSuppliers : []).map(parseSupplierRow),
    byCategory: (Array.isArray(raw.byCategory) ? raw.byCategory : []).map(parseSlice),
    byBu: (Array.isArray(raw.byBu) ? raw.byBu : []).map(parseSlice),
    byBand: {
      high: num(band.high),
      medium: num(band.medium),
      low: num(band.low),
      unscored: num(band.unscored),
    },
    insights: insights.map((row) => {
      const r = asRecord(row)
      const kind = String(r.kind || 'hotspot')
      return {
        id: String(r.id || r.text || kind),
        kind: (kind === 'single_source' || kind === 'data_quality' || kind === 'exposure' || kind === 'mitigation'
          ? kind
          : 'hotspot') as PortfolioInsight['kind'],
        text: String(r.text || ''),
      }
    }),
    years: (Array.isArray(raw.years) ? raw.years : []).map((y) => num(y)).filter((y) => y > 0),
    bus: (Array.isArray(raw.bus) ? raw.bus : []).map((v) => String(v)),
    categories: (Array.isArray(raw.categories) ? raw.categories : []).map((v) => String(v)),
  }
}

function parseSupplierRow(row: unknown): SpendSupplierRow {
  const r = asRecord(row)
  return {
    parentName: String(r.parentName || ''),
    supplierId: r.supplierId ? String(r.supplierId) : null,
    spend: num(r.spend),
    share: numOrNull(r.share),
    band: r.band ? String(r.band) : null,
    score: numOrNull(r.score),
    priorityLine: r.priorityLine ? String(r.priorityLine) : null,
    linked: r.linked == null ? Boolean(r.supplierId) : Boolean(r.linked),
  }
}

function parseSlice(row: unknown): SpendSlice {
  const r = asRecord(row)
  return { key: String(r.key || ''), spend: num(r.spend), share: numOrNull(r.share) }
}

export async function fetchSpendSupplier(
  companyId: string,
  parentName: string,
  filters: SpendFilters
): Promise<SpendSupplierSynthesis> {
  const raw = asRecord(
    await rpc('spend_supplier_synthesis', {
      p_company_id: companyId,
      p_parent_name: parentName,
      p_year: filters.year,
      p_bu: filters.bu || null,
      p_rollup_2: filters.rollup || null,
    })
  )
  if (!raw.hasData) return { hasData: false, reason: raw.reason ? String(raw.reason) : undefined }
  return {
    hasData: true,
    parentName: String(raw.parentName || parentName),
    supplierId: raw.supplierId ? String(raw.supplierId) : null,
    supplierName: raw.supplierName ? String(raw.supplierName) : null,
    location: raw.location ? String(raw.location) : null,
    category: raw.category ? String(raw.category) : null,
    region: raw.region ? String(raw.region) : null,
    linked: Boolean(raw.linked),
    spend: num(raw.spend),
    share: numOrNull(raw.share),
    yoy: numOrNull(raw.yoy),
    categoryCount: num(raw.categoryCount),
    poUtilization: numOrNull(raw.poUtilization),
    consultantShare: numOrNull(raw.consultantShare),
    score: numOrNull(raw.score),
    band: raw.band ? String(raw.band) : null,
    singleSource: raw.singleSource == null ? null : Boolean(raw.singleSource),
    topRiskLine: raw.topRiskLine ? String(raw.topRiskLine) : null,
    outlook: raw.outlook ? String(raw.outlook) : null,
    openMitigations: num(raw.openMitigations),
    priorityLine: raw.priorityLine ? String(raw.priorityLine) : null,
  }
}

export async function fetchSpendCategory(
  companyId: string,
  category: string,
  filters: Pick<SpendFilters, 'year' | 'bu'>
): Promise<SpendCategorySynthesis> {
  const raw = asRecord(
    await rpc('spend_category_synthesis', {
      p_company_id: companyId,
      p_rollup_2: category,
      p_year: filters.year,
      p_bu: filters.bu || null,
    })
  )
  if (!raw.hasData) {
    return { hasData: false, reason: raw.reason ? String(raw.reason) : undefined, category }
  }
  return {
    hasData: true,
    category: String(raw.category || category),
    spend: num(raw.spend),
    share: numOrNull(raw.share),
    yoy: numOrNull(raw.yoy),
    supplierCount: num(raw.supplierCount),
    topSupplierShare: numOrNull(raw.topSupplierShare),
    weightedRisk: numOrNull(raw.weightedRisk),
    suppliers: (Array.isArray(raw.suppliers) ? raw.suppliers : []).map(parseSupplierRow),
  }
}

export type SpendYearAmount = { year: number; spend: number }

export type SpendYearRow = {
  key: string
  spend: number
  years: SpendYearAmount[]
}

export type SpendSupplierYear = {
  parentName: string
  spend: number
  years: SpendYearAmount[]
}

export type SpendBuYear = {
  year: number
  spend: number
  sites: SpendSlice[]
}

export type SpendRollupYear = {
  reportRollup: string
  rollup2: string
  spend: number
  years: SpendYearAmount[]
}

export type SpendPoLineYear = {
  poNumber: string
  description: string
  spend: number
  years: SpendYearAmount[]
}

export type SpendConcentrationPoint = {
  rank: number
  parentName: string
  cumulativeShare: number | null
}

export type SpendHeadroomRow = {
  parentName: string
  poValue: number
  invoiced: number
  remaining: number
  utilization: number | null
}

export type SpendPoStatus = {
  key: string
  commitment: number
  invoice: number
}

export type SpendBudgetQuarter = {
  year: number
  quarter: number
  budget: number
  spend: number
  ratio: number | null
}

export type SpendCubeViews = {
  hasData: boolean
  activeSuppliers: number
  topSuppliers: SpendSlice[]
  concentration: SpendConcentrationPoint[]
  tailRank: number | null
  bySap: SpendSlice[]
  byPlant: SpendSlice[]
  byYear: SpendYearAmount[]
  suppliersByYear: SpendSupplierYear[]
  statusByYear: SpendYearRow[]
  contractorByYear: SpendYearRow[]
  buByYear: SpendBuYear[]
  reportRollups: SpendRollupYear[]
  topPoLines: SpendPoLineYear[]
  materialByYear: SpendYearRow[]
  plantByYear: SpendYearRow[]
  po: {
    hasPo: boolean
    commitment: number
    poInvoice: number
    utilization: number | null
    byStatus: SpendPoStatus[]
    headroom: SpendHeadroomRow[]
  }
  budget: {
    hasBudget: boolean
    totalBudget: number
    latestYear: number | null
    latestBudget: number
    latestSpend: number
    quarters: SpendBudgetQuarter[]
  }
}

export type SpendCubeSummary = {
  hasData: boolean
  byYear: SpendYearAmount[]
  suppliersByYear: SpendSupplierYear[]
  statusByYear: SpendYearRow[]
  contractorByYear: SpendYearRow[]
  buByYear: SpendBuYear[]
  reportRollups: SpendRollupYear[]
  topPoLines: SpendPoLineYear[]
  materialByYear: SpendYearRow[]
  plantByYear: SpendYearRow[]
  options: {
    years: number[]
    costCenters: string[]
    bus: string[]
    statuses: string[]
    departments: string[]
    rollups: string[]
    accountNames: string[]
    accountRollups: string[]
    reportRollups: string[]
    contractors: string[]
    parents: string[]
    sapItems: string[]
    materials: string[]
    plants: string[]
  }
}

function parseYears(value: unknown): SpendYearAmount[] {
  return (Array.isArray(value) ? value : []).map((row) => {
    const y = asRecord(row)
    return { year: num(y.year), spend: num(y.spend) }
  })
}

function parseYearRow(row: unknown): SpendYearRow {
  const r = asRecord(row)
  return { key: String(r.key || ''), spend: num(r.spend), years: parseYears(r.years) }
}

const emptyPo = (): SpendCubeViews['po'] => ({
  hasPo: false,
  commitment: 0,
  poInvoice: 0,
  utilization: null,
  byStatus: [],
  headroom: [],
})

const emptyViews = (): SpendCubeViews => ({
  hasData: false,
  activeSuppliers: 0,
  topSuppliers: [],
  concentration: [],
  tailRank: null,
  bySap: [],
  byPlant: [],
  byYear: [],
  suppliersByYear: [],
  statusByYear: [],
  contractorByYear: [],
  buByYear: [],
  reportRollups: [],
  topPoLines: [],
  materialByYear: [],
  plantByYear: [],
  po: emptyPo(),
  budget: { hasBudget: false, totalBudget: 0, latestYear: null, latestBudget: 0, latestSpend: 0, quarters: [] },
})

export async function fetchSpendCubeViews(
  companyId: string,
  filters: SpendFilters
): Promise<SpendCubeViews> {
  const raw = asRecord(
    await rpc('spend_cube_views', {
      p_company_id: companyId,
      p_year: filters.year,
      p_bu: filters.bu || null,
      p_rollup_2: filters.rollup || null,
    })
  )
  if (!raw.hasData) return emptyViews()
  const budget = asRecord(raw.budget)
  const po = asRecord(raw.po)
  return {
    hasData: true,
    activeSuppliers: num(raw.activeSuppliers),
    topSuppliers: (Array.isArray(raw.topSuppliers) ? raw.topSuppliers : []).map((row) => {
      const r = asRecord(row)
      return { key: String(r.parentName || ''), spend: num(r.spend), share: numOrNull(r.share) }
    }),
    concentration: (Array.isArray(raw.concentration) ? raw.concentration : []).map((row) => {
      const r = asRecord(row)
      return { rank: num(r.rank), parentName: String(r.parentName || ''), cumulativeShare: numOrNull(r.cumulativeShare) }
    }),
    tailRank: numOrNull(raw.tailRank),
    bySap: (Array.isArray(raw.bySap) ? raw.bySap : []).map(parseSlice),
    byPlant: (Array.isArray(raw.byPlant) ? raw.byPlant : []).map(parseSlice),
    byYear: parseYears(raw.byYear),
    suppliersByYear: (Array.isArray(raw.suppliersByYear) ? raw.suppliersByYear : []).map((row) => {
      const r = asRecord(row)
      return { parentName: String(r.parentName || ''), spend: num(r.spend), years: parseYears(r.years) }
    }),
    statusByYear: (Array.isArray(raw.statusByYear) ? raw.statusByYear : []).map(parseYearRow),
    contractorByYear: (Array.isArray(raw.contractorByYear) ? raw.contractorByYear : []).map(parseYearRow),
    buByYear: (Array.isArray(raw.buByYear) ? raw.buByYear : []).map((row) => {
      const r = asRecord(row)
      return {
        year: num(r.year),
        spend: num(r.spend),
        sites: (Array.isArray(r.sites) ? r.sites : []).map(parseSlice),
      }
    }),
    reportRollups: (Array.isArray(raw.reportRollups) ? raw.reportRollups : []).map((row) => {
      const r = asRecord(row)
      return {
        reportRollup: String(r.reportRollup || ''),
        rollup2: String(r.rollup2 || ''),
        spend: num(r.spend),
        years: parseYears(r.years),
      }
    }),
    topPoLines: (Array.isArray(raw.topPoLines) ? raw.topPoLines : []).map((row) => {
      const r = asRecord(row)
      return {
        poNumber: String(r.poNumber || ''),
        description: String(r.description || ''),
        spend: num(r.spend),
        years: parseYears(r.years),
      }
    }),
    materialByYear: (Array.isArray(raw.materialByYear) ? raw.materialByYear : []).map(parseYearRow),
    plantByYear: (Array.isArray(raw.plantByYear) ? raw.plantByYear : []).map(parseYearRow),
    po: po.hasPo
      ? {
          hasPo: true,
          commitment: num(po.commitment),
          poInvoice: num(po.poInvoice),
          utilization: numOrNull(po.utilization),
          byStatus: (Array.isArray(po.byStatus) ? po.byStatus : []).map((row) => {
            const s = asRecord(row)
            return { key: String(s.key || ''), commitment: num(s.commitment), invoice: num(s.invoice) }
          }),
          headroom: (Array.isArray(po.headroom) ? po.headroom : []).map((row) => {
            const h = asRecord(row)
            return {
              parentName: String(h.parentName || ''),
              poValue: num(h.poValue),
              invoiced: num(h.invoiced),
              remaining: num(h.remaining),
              utilization: numOrNull(h.utilization),
            }
          }),
        }
      : emptyPo(),
    budget: {
      hasBudget: Boolean(budget.hasBudget),
      totalBudget: num(budget.totalBudget),
      latestYear: numOrNull(budget.latestYear),
      latestBudget: num(budget.latestBudget),
      latestSpend: num(budget.latestSpend),
      quarters: (Array.isArray(budget.quarters) ? budget.quarters : []).map((row) => {
        const q = asRecord(row)
        return {
          year: num(q.year),
          quarter: num(q.quarter),
          budget: num(q.budget),
          spend: num(q.spend),
          ratio: numOrNull(q.ratio),
        }
      }),
    },
  }
}

function parseStringList(value: unknown): string[] {
  return (Array.isArray(value) ? value : []).map((item) => String(item))
}

const emptySummary = (): SpendCubeSummary => ({
  hasData: false,
  byYear: [],
  suppliersByYear: [],
  statusByYear: [],
  contractorByYear: [],
  buByYear: [],
  reportRollups: [],
  topPoLines: [],
  materialByYear: [],
  plantByYear: [],
  options: {
    years: [],
    costCenters: [],
    bus: [],
    statuses: [],
    departments: [],
    rollups: [],
    accountNames: [],
    accountRollups: [],
    reportRollups: [],
    contractors: [],
    parents: [],
    sapItems: [],
    materials: [],
    plants: [],
  },
})

export async function fetchSpendCubeSummary(
  companyId: string,
  filters: SpendSummaryFilters
): Promise<SpendCubeSummary> {
  const raw = asRecord(
    await rpc('spend_cube_summary', {
      p_company_id: companyId,
      p_filters: filters,
    })
  )
  if (!raw.hasData) return emptySummary()
  const options = asRecord(raw.options)
  return {
    hasData: true,
    byYear: parseYears(raw.byYear),
    suppliersByYear: (Array.isArray(raw.suppliersByYear) ? raw.suppliersByYear : []).map((row) => {
      const r = asRecord(row)
      return { parentName: String(r.parentName || ''), spend: num(r.spend), years: parseYears(r.years) }
    }),
    statusByYear: (Array.isArray(raw.statusByYear) ? raw.statusByYear : []).map(parseYearRow),
    contractorByYear: (Array.isArray(raw.contractorByYear) ? raw.contractorByYear : []).map(parseYearRow),
    buByYear: (Array.isArray(raw.buByYear) ? raw.buByYear : []).map((row) => {
      const r = asRecord(row)
      return {
        year: num(r.year),
        spend: num(r.spend),
        sites: (Array.isArray(r.sites) ? r.sites : []).map(parseSlice),
      }
    }),
    reportRollups: (Array.isArray(raw.reportRollups) ? raw.reportRollups : []).map((row) => {
      const r = asRecord(row)
      return {
        reportRollup: String(r.reportRollup || ''),
        rollup2: String(r.rollup2 || ''),
        spend: num(r.spend),
        years: parseYears(r.years),
      }
    }),
    topPoLines: (Array.isArray(raw.topPoLines) ? raw.topPoLines : []).map((row) => {
      const r = asRecord(row)
      return {
        poNumber: String(r.poNumber || ''),
        description: String(r.description || ''),
        spend: num(r.spend),
        years: parseYears(r.years),
      }
    }),
    materialByYear: (Array.isArray(raw.materialByYear) ? raw.materialByYear : []).map(parseYearRow),
    plantByYear: (Array.isArray(raw.plantByYear) ? raw.plantByYear : []).map(parseYearRow),
    options: {
      years: (Array.isArray(options.years) ? options.years : []).map((year) => num(year)).filter((year) => year > 0),
      costCenters: parseStringList(options.costCenters),
      bus: parseStringList(options.bus),
      statuses: parseStringList(options.statuses),
      departments: parseStringList(options.departments),
      rollups: parseStringList(options.rollups),
      accountNames: parseStringList(options.accountNames),
      accountRollups: parseStringList(options.accountRollups),
      reportRollups: parseStringList(options.reportRollups),
      contractors: parseStringList(options.contractors),
      parents: parseStringList(options.parents),
      sapItems: parseStringList(options.sapItems),
      materials: parseStringList(options.materials),
      plants: parseStringList(options.plants),
    },
  }
}

export type SpendCubeDim =
  | 'parent'
  | 'rollup'
  | 'year'
  | 'bu'
  | 'plant'
  | 'material'
  | 'department'
  | 'status'
  | 'contractor'

export type SpendCubeFaceAxis = { key: string; spend: number }
export type SpendCubeFaceCell = { row: string; column: string; spend: number }

export type SpendCubeFace = {
  hasData: boolean
  total: number
  rows: SpendCubeFaceAxis[]
  columns: SpendCubeFaceAxis[]
  cells: SpendCubeFaceCell[]
}

export async function fetchSpendCubeFace(
  companyId: string,
  rowDim: SpendCubeDim,
  colDim: SpendCubeDim,
  filters: SpendSummaryFilters
): Promise<SpendCubeFace> {
  const raw = asRecord(
    await rpc('spend_cube_face', {
      p_company_id: companyId,
      p_row_dim: rowDim,
      p_col_dim: colDim,
      p_filters: filters,
    })
  )
  if (!raw.hasData) return { hasData: false, total: 0, rows: [], columns: [], cells: [] }
  const axis = (value: unknown): SpendCubeFaceAxis[] =>
    (Array.isArray(value) ? value : []).map((row) => {
      const record = asRecord(row)
      return { key: String(record.key || ''), spend: num(record.spend) }
    })
  return {
    hasData: true,
    total: num(raw.total),
    rows: axis(raw.rows),
    columns: axis(raw.columns),
    cells: (Array.isArray(raw.cells) ? raw.cells : []).map((row) => {
      const record = asRecord(row)
      return { row: String(record.row || ''), column: String(record.column || ''), spend: num(record.spend) }
    }),
  }
}
